import { VerifiedBadge } from "../components/VerifiedBadge";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "../components/Navbar";
import { api, BACKEND_URL } from "../api";
import { Avatar } from "../components/Posts";
import { formatProName } from "../lib/proName";
import { SlidersHorizontal, X } from "lucide-react";
import { orgTypeLabel } from "../lib/orgTypes";

const TYPES = ["all", "professionals", "organizations", "events", "services", "products"];
const eur = (c) => (c === 0 ? "Gratis" : c != null ? `€${(c / 100).toFixed(2)}` : "");
// Evidenzia il termine cercato nel titolo (discreto, stile YouPAM)
function Highlight({ text, q }) {
  const s = text == null ? "" : String(text);
  const term = (q || "").trim();
  if (!term) return s;
  const rx = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig");
  return s.split(rx).map((p, i) =>
    p && p.toLowerCase() === term.toLowerCase()
      ? <mark key={i} className="rounded bg-amber-100 px-0.5 text-inherit" data-testid="search-hl">{p}</mark>
      : <span key={i}>{p}</span>);
}
// Filtri contestuali per tipologia (solo campi che i dati reali consentono)
const FILTERS = {
  professionals: ["city", "specialization"],
  organizations: ["city", "org_type"],
  events: ["city", "date", "free"],
  services: ["city", "price"],
  products: ["category", "price"],
};

export default function SearchResults() {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const q = sp.get("q") || "";
  const type = TYPES.includes(sp.get("type")) ? sp.get("type") : "all";
  const filt = {
    city: sp.get("city") || "", specialization: sp.get("specialization") || "",
    org_type: sp.get("org_type") || "", date: sp.get("date") || "",
    free: sp.get("free") || "", category: sp.get("category") || "",
    price_min: sp.get("price_min") || "", price_max: sp.get("price_max") || "",
  };
  const [all, setAll] = useState(null);
  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [deskFilters, setDeskFilters] = useState(false);
  const [facets, setFacets] = useState({ specializations: [], org_types: [], categories: [] });
  const [qInput, setQInput] = useState(q);

  useEffect(() => { setQInput(q); }, [q]);
  // Ricerca dinamica: debounce → aggiorna q in URL (mantiene type/filtri, resetta pagina). Vuoto → browse.
  useEffect(() => {
    const val = qInput.trim();
    if (val === q) return;
    const id = setTimeout(() => setParam({ q: val }), 350);
    return () => clearTimeout(id);
  }, [qInput]); // eslint-disable-line

  useEffect(() => {
    api.get("/search-facets").then(({ data }) => setFacets(data)).catch(() => {});
  }, []);

  const setParam = (patch) => {
    const n = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => { if (v === "" || v == null) n.delete(k); else n.set(k, v); });
    setSp(n, { replace: false });
  };
  const goType = (tp) => setParam({ type: tp === "all" ? "" : tp });
  const clearFilters = () => {
    const n = new URLSearchParams(); if (q) n.set("q", q); if (type !== "all") n.set("type", type); setSp(n);
  };

  // "Tutto": anteprime 5 categorie
  useEffect(() => {
    if (type !== "all") return;
    setLoading(true);
    api.get("/search-global", { params: { q } }).then(({ data }) => setAll(data)).catch(() => setAll(null)).finally(() => setLoading(false));
  }, [q, type]);

  // Tipologia specifica: paginazione
  useEffect(() => {
    if (type === "all") return;
    setPage(1); setList([]); setLoading(true);
    const params = { type, q, page: 1, page_size: 12 };
    if (filt.city) params.city = filt.city;
    if (filt.specialization) params.category = filt.specialization;
    if (filt.org_type) params.category = filt.org_type;
    if (filt.category) params.category = filt.category;
    if (filt.date) params.date = filt.date;
    if (filt.free) params.free = filt.free;
    if (filt.price_min) params.price_min = Math.round(Number(filt.price_min) * 100);
    if (filt.price_max) params.price_max = Math.round(Number(filt.price_max) * 100);
    api.get("/search-results", { params }).then(({ data }) => {
      setList(data.items); setHasMore(data.has_more); setTotal(data.total);
    }).catch(() => { setList([]); setHasMore(false); setTotal(0); }).finally(() => setLoading(false));
  }, [type, q, sp.toString()]); // eslint-disable-line

  const loadMore = () => {
    const np = page + 1;
    const params = { type, q, page: np, page_size: 12 };
    if (filt.city) params.city = filt.city;
    if (filt.specialization) params.category = filt.specialization;
    if (filt.org_type) params.category = filt.org_type;
    if (filt.category) params.category = filt.category;
    if (filt.date) params.date = filt.date;
    if (filt.free) params.free = filt.free;
    if (filt.price_min) params.price_min = Math.round(Number(filt.price_min) * 100);
    if (filt.price_max) params.price_max = Math.round(Number(filt.price_max) * 100);
    api.get("/search-results", { params }).then(({ data }) => {
      setList(l => [...l, ...data.items]); setHasMore(data.has_more); setPage(np);
    });
  };

  const activeFilters = FILTERS[type] || [];
  const hasActiveFilterValues = ["city", "specialization", "org_type", "date", "free", "category", "price_min", "price_max"].some(k => filt[k]);

  const FiltersForm = () => (
    <div className="space-y-3" data-testid="search-filters-form">
      {activeFilters.includes("city") && (
        <input className="yp-input w-full" placeholder={t("searchpage.city")} defaultValue={filt.city}
          onBlur={e => setParam({ city: e.target.value.trim() })} onKeyDown={e => e.key === "Enter" && setParam({ city: e.target.value.trim() })}
          data-testid="filter-city" />)}
      {activeFilters.includes("specialization") && (
        <select className="yp-input w-full" value={filt.specialization}
          onChange={e => setParam({ specialization: e.target.value })} data-testid="filter-specialization">
          <option value="">{t("searchpage.all_specializations")}</option>
          {facets.specializations.map(v => <option key={v} value={v}>{v}</option>)}
        </select>)}
      {activeFilters.includes("org_type") && (
        <select className="yp-input w-full" value={filt.org_type}
          onChange={e => setParam({ org_type: e.target.value })} data-testid="filter-org-type">
          <option value="">{t("searchpage.all_org_types")}</option>
          {facets.org_types.map(v => <option key={v} value={v}>{v}</option>)}
        </select>)}
      {activeFilters.includes("date") && (
        <input type="date" className="yp-input w-full" defaultValue={filt.date}
          onChange={e => setParam({ date: e.target.value })} data-testid="filter-date" />)}
      {activeFilters.includes("free") && (
        <select className="yp-input w-full" value={filt.free} onChange={e => setParam({ free: e.target.value })} data-testid="filter-free">
          <option value="">{t("searchpage.any")}</option>
          <option value="1">{t("searchpage.free")}</option>
          <option value="0">{t("searchpage.paid")}</option>
        </select>)}
      {activeFilters.includes("category") && (
        <select className="yp-input w-full" value={filt.category}
          onChange={e => setParam({ category: e.target.value })} data-testid="filter-category">
          <option value="">{t("searchpage.all_categories")}</option>
          {facets.categories.map(v => <option key={v} value={v}>{v}</option>)}
        </select>)}
      {activeFilters.includes("price") && (
        <div className="flex gap-2">
          <input type="number" min="0" className="yp-input w-full" placeholder={t("searchpage.price_min")} defaultValue={filt.price_min}
            onBlur={e => setParam({ price_min: e.target.value })} data-testid="filter-price-min" />
          <input type="number" min="0" className="yp-input w-full" placeholder={t("searchpage.price_max")} defaultValue={filt.price_max}
            onBlur={e => setParam({ price_max: e.target.value })} data-testid="filter-price-max" />
        </div>)}
      {hasActiveFilterValues && (
        <button className="yp-btn yp-btn-ghost text-blue-600 !px-0 text-sm" onClick={clearFilters} data-testid="clear-filters">{t("searchpage.clear_filters")}</button>)}
    </div>);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4" data-testid="search-results-page">
        <h1 className="text-2xl font-bold text-slate-800">{t("searchpage.title")}</h1>
        {q && <p className="text-sm text-slate-500 mt-1" data-testid="search-query-label">{t("searchpage.results_for")} “{q}”</p>}

        <div className="mt-3">
          <input className="yp-input w-full max-w-2xl" placeholder={t("searchpage.search_here")} value={qInput}
            onChange={e => setQInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") setParam({ q: qInput.trim() }); }}
            data-testid="search-page-input" aria-label={t("searchpage.search_here")} />
        </div>

        {/* Desktop: toggle filtri contestuali (nascosti di default). Nascosto in "Tutto" (nessun filtro globale). */}
        {type !== "all" && activeFilters.length > 0 && (
          <div className="hidden lg:block">
            <button className="mt-3 yp-btn yp-btn-outline inline-flex items-center gap-2 text-sm"
              onClick={() => setDeskFilters(o => !o)} data-testid="desktop-filters-btn" aria-expanded={deskFilters}>
              <SlidersHorizontal className="w-4 h-4" /> {t("searchpage.filters")}
            </button>
          </div>)}

        {/* Mobile tabs */}
        <div className="lg:hidden mt-4 -mx-3 px-3 overflow-x-auto">
          <div className="flex gap-2 w-max pb-1">
            {TYPES.map(tp => (
              <button key={tp} onClick={() => goType(tp)}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium ${type === tp ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"}`}
                data-testid={`tab-${tp}`}>{t(`searchpage.${tp}`)}</button>))}
          </div>
        </div>
        {type !== "all" && (activeFilters.length > 0) && (
          <div className="lg:hidden">
            <button className="mt-3 yp-btn yp-btn-outline inline-flex items-center gap-2 text-sm" onClick={() => setSheet(true)} data-testid="mobile-filters-btn">
              <SlidersHorizontal className="w-4 h-4" /> {t("searchpage.filters")}
            </button>
          </div>)}

        <div className="mt-4 flex gap-6">
          {/* Desktop sidebar */}
          <aside className="hidden lg:block w-60 shrink-0" data-testid="search-sidebar">
            <div className="yp-card p-3 sticky top-4">
              <div className="text-xs font-semibold text-slate-400 uppercase px-2 mb-1">{t("searchpage.title")}</div>
              {TYPES.map(tp => (
                <button key={tp} onClick={() => goType(tp)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm ${type === tp ? "bg-blue-50 text-blue-700 font-semibold" : "hover:bg-slate-50 text-slate-600"}`}
                  data-testid={`side-${tp}`}>{t(`searchpage.${tp}`)}</button>))}
              {type !== "all" && activeFilters.length > 0 && deskFilters && (
                <div className="border-t border-slate-100 mt-3 pt-3" data-testid="desktop-filters-panel">
                  <div className="text-xs font-semibold text-slate-400 uppercase px-1 mb-2">{t("searchpage.filters")}</div>
                  <FiltersForm />
                </div>)}
            </div>
          </aside>

          {/* Main */}
          <main className="flex-1 min-w-0">
            {loading && list.length === 0 && type !== "all" && <div className="text-slate-400 py-8">…</div>}

            {type === "all" ? (
              <AllView all={all} q={q} nav={nav} t={t} i18n={i18n} />
            ) : (
              <>
                {!loading && list.length === 0 ? (
                  <div className="yp-card p-8 text-center" data-testid="no-results">
                    <div className="font-semibold text-slate-700">{t("searchpage.no_results")}</div>
                    <div className="text-sm text-slate-500 mt-1">{t("searchpage.no_results_hint")}</div>
                  </div>
                ) : (
                  <>
                    <div className="text-sm text-slate-500 mb-3">{total} {t(total === 1 ? "searchpage.count_one" : "searchpage.count_other")}</div>
                    <ResultGrid type={type} items={list} t={t} i18n={i18n} q={q} />
                    {hasMore && <div className="text-center mt-5"><button className="yp-btn yp-btn-outline" onClick={loadMore} data-testid="load-more">{t("searchpage.load_more")}</button></div>}
                  </>)}
              </>)}
          </main>
        </div>
      </div>

      {/* Mobile filters bottom-sheet */}
      {sheet && (
        <div className="lg:hidden fixed inset-0 z-[70] bg-black/40 flex items-end" onClick={() => setSheet(false)} data-testid="mobile-filters-sheet">
          <div className="bg-white w-full rounded-t-2xl p-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-bold">{t("searchpage.filters")}</div>
              <button onClick={() => setSheet(false)} className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center" data-testid="mobile-filters-close"><X className="w-5 h-5" /></button>
            </div>
            <FiltersForm />
          </div>
        </div>)}
    </Layout>);
}

function AllView({ all, q, nav, t, i18n }) {
  if (!all) return <div className="text-slate-400 py-8">…</div>;
  const empty = ["professionals", "organizations", "events", "services", "products"].every(k => (all[k] || []).length === 0);
  if (empty) return (
    <div className="yp-card p-8 text-center" data-testid="all-no-results">
      <div className="font-semibold text-slate-700">{t("searchpage.no_results")}</div>
      <div className="text-sm text-slate-500 mt-1">{t("searchpage.no_results_hint")}</div>
    </div>);
  const block = (key) => (all[key] || []).length > 0 && (
    <section className="mb-6" data-testid={`all-block-${key}`}>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-bold text-slate-800">{t(`searchpage.${key}`)}</h2>
        <button className="text-sm text-blue-600 hover:underline" onClick={() => nav(`/search?${new URLSearchParams({ ...(q ? { q } : {}), type: key })}`)} data-testid={`all-seeall-${key}`}>{t("searchpage.see_all")}</button>
      </div>
      <ResultGrid type={key} items={all[key]} t={t} i18n={i18n} q={q} />
    </section>);
  return <div>{["professionals", "organizations", "events", "services", "products"].map(k => <div key={k}>{block(k)}</div>)}</div>;
}

// Immagine con fallback a placeholder neutro se assente o non caricabile
function ImgOrPlaceholder({ src, className, phClassName }) {
  const [broken, setBroken] = useState(false);
  if (!src || broken) return <div className={phClassName} data-testid="img-placeholder" />;
  return <img src={src} alt="" className={className} onError={() => setBroken(true)} data-testid="result-img" />;
}
// Risolve il valore immagine (URL esterno, path /api relativo, oppure id nudo) in un src caricabile
const resolveImg = (v) => !v ? "" : /^https?:\/\//i.test(v) ? v : v.startsWith("/") ? `${BACKEND_URL}${v}` : `${BACKEND_URL}/api/product-images/${v}`;

function ResultGrid({ type, items, t, i18n, q }) {
  if (type === "products") {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="products-grid">
        {items.map(p => (
          <Link key={p.id} to={`/products/${p.id}`} className="yp-card overflow-hidden hover:shadow-md transition-shadow" data-testid="result-product">
            <ImgOrPlaceholder src={resolveImg(p.image_id)} className="w-full aspect-square object-cover" phClassName="w-full aspect-square bg-slate-100" />
            <div className="p-2">
              <div className="text-sm font-medium truncate"><Highlight text={p.name} q={q} /></div>
              <div className="text-xs text-slate-500 truncate">{p.org_name}</div>
              <div className="text-sm font-bold mt-1">{p.price_from ? "da " : ""}{eur(p.price_cents)}{p.out_of_stock && <span className="ml-1 text-xs text-red-500 font-normal">· {t("shop.out_of_stock")}</span>}</div>
            </div>
          </Link>))}
      </div>);
  }
  return (
    <div className="space-y-2" data-testid={`results-${type}`}>
      {items.map(it => {
        if (type === "professionals") return (
          <Link key={it.id} to={`/professionals/${it.id}`} className="yp-card p-3 flex items-center gap-3 hover:shadow-md" data-testid="result-professional">
            <Avatar url={it.avatar_url} label={it.first_name} cls="w-11 h-11" />
            <div className="min-w-0">
              <div className="font-medium truncate flex items-center gap-1"><Highlight text={formatProName({ title: it.professional_title, first_name: it.first_name, last_name: it.last_name }, i18n.language)} q={q} />{it.verified && <VerifiedBadge size={15} />}</div>
              <div className="text-xs text-slate-500 truncate">{[it.specialization, it.city].filter(Boolean).join(" · ")}</div>
            </div>
          </Link>);
        if (type === "organizations") return (
          <Link key={it.id} to={`/organizations/${it.id}`} className="yp-card p-3 flex items-center gap-3 hover:shadow-md" data-testid="result-organization">
            <Avatar url={it.avatar_url} label={it.org_name} org cls="w-11 h-11" />
            <div className="min-w-0">
              <div className="font-medium truncate flex items-center gap-1"><Highlight text={it.org_name} q={q} />{it.verified && <VerifiedBadge size={15} />}</div>
              <div className="text-xs text-slate-500 truncate">{[orgTypeLabel(it.org_type, t, it.org_type_other), it.city, it.country].filter(Boolean).join(" · ")}</div>
            </div>
          </Link>);
        if (type === "events") return (
          <Link key={it.id} to={`/events/${it.id}`} className="yp-card p-3 flex items-center gap-3 hover:shadow-md" data-testid="result-event">
            <ImgOrPlaceholder src={resolveImg(it.organization_avatar_url) || resolveImg(it.image_url)} className="w-14 h-14 rounded-lg object-cover shrink-0" phClassName="w-14 h-14 rounded-lg bg-slate-100 shrink-0" />
            <div className="min-w-0">
              <div className="font-medium truncate"><Highlight text={it.title} q={q} /></div>
              <div className="text-xs text-slate-500 truncate">{[it.organizer_name, it.start_at ? new Date(it.start_at).toLocaleDateString(i18n.language) : null, it.city].filter(Boolean).join(" · ")}</div>
              <div className="text-xs font-semibold mt-0.5">{eur(it.price_cents ?? 0)}</div>
            </div>
          </Link>);
        if (type === "services") return (
          <Link key={it.id} to={`/services/${it.id}`} className="yp-card p-3 flex items-center gap-3 hover:shadow-md" data-testid="result-service">
            <ImgOrPlaceholder src={resolveImg(it.org_avatar_url)} className="w-11 h-11 rounded-lg object-cover shrink-0" phClassName="w-11 h-11 rounded-lg bg-blue-50 shrink-0" />
            <div className="min-w-0">
              <div className="font-medium truncate"><Highlight text={it.name} q={q} /></div>
              <div className="text-xs text-slate-500 truncate">{[it.org_name, it.professional_name, it.location_label, it.duration_minutes ? `${it.duration_minutes}′` : null, eur(it.price_cents)].filter(Boolean).join(" · ")}</div>
            </div>
          </Link>);
        return null;
      })}
    </div>);
}
